import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link, SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import WonItemRow from '@/pages/WonItems/components/WonItemsCard/components/WonItemRow/WonItemRow';
import { LocationInfo } from '@/redux/modules/wonItems/wonItems.types';
import { WonCatalog } from '@/types/WonCatalog';
import { WonItemInfo } from '@/types/WonItem';
import { getGoogleMapUrlFromAddress } from '@/utils/urls';

type Props = {
    locationInfo: LocationInfo;
    wonCatalog: WonCatalog;
};

const WonItemLocation = ({ locationInfo, wonCatalog }: Props) => {
    const { formatMessage } = useIntl();
    const formattedAddress = `${locationInfo.city}, ${locationInfo.region} ${locationInfo.country}`;

    const [wonItems, setWonItems] = useState<WonItemInfo[]>([]);

    const [mapsUrl, setMapsUrl] = useState('');

    useEffect(() => {
        setMapsUrl(
            getGoogleMapUrlFromAddress({
                city: locationInfo.city,
                country: locationInfo.country,
                region: locationInfo.region,
            })
        );
    }, [locationInfo]);

    useEffect(() => {
        setWonItems(
            wonCatalog.items.map((item) => ({
                ...item,
                catalogId: wonCatalog.catalogId,
                currency: wonCatalog.currency,
                sellerId: wonCatalog.sellerId,
            }))
        );
    }, [wonCatalog]);

    let shippingInfo: JSX.Element = null;
    if (locationInfo.shippingStatus) {
        let subtitle: JSX.Element = null;
        // additional information shown/hidden based on available data
        if (locationInfo.carrierName) {
            if (locationInfo.shippingTrackingLink) {
                // shipped via {carrier}. view tracking link
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(
                            {
                                id: 'wonitems.itemsByLocation.shippedBy',
                            },
                            {
                                carrier: locationInfo.carrierName,
                            }
                        )}{' '}
                        <Link
                            href={locationInfo.shippingTrackingLink}
                            size="sm"
                        >
                            {formatMessage({
                                id: 'wonitems.itemsByLocation.viewTrackingHistory',
                            })}
                        </Link>
                    </div>
                );
            } else if (locationInfo.shippingTrackingNumber) {
                // shipped via {carrier}. Tracking number: 32508235
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(
                            {
                                id: 'wonitems.itemsByLocation.shippedBy',
                            },
                            {
                                carrier: locationInfo.carrierName,
                            }
                        )}{' '}
                        {formatMessage(
                            {
                                id: 'wonitems.itemsByLocation.trackingNumber',
                            },
                            {
                                trackingNumber: locationInfo.shippingTrackingNumber,
                            }
                        )}
                    </div>
                );
            } else {
                // shipped via {carrier}
                subtitle = (
                    <div className="hui-text-body-secondary text-text-secondary">
                        {locationInfo.shippingStatusSecondary}{' '}
                        {formatMessage(
                            {
                                id: 'wonitems.itemsByLocation.shippedBy',
                            },
                            {
                                carrier: locationInfo.carrierName,
                            }
                        )}
                    </div>
                );
            }
        } else if (locationInfo.shippingTrackingLink) {
            // view tracking link
            subtitle = (
                <div className="hui-text-body-secondary text-text-secondary">
                    {locationInfo.shippingStatusSecondary}{' '}
                    <Link
                        href={locationInfo.shippingTrackingLink}
                        size="sm"
                    >
                        {formatMessage({
                            id: 'wonitems.itemsByLocation.viewTrackingHistory',
                        })}
                    </Link>
                </div>
            );
        } else {
            // otherwise, nothing else is shown
            subtitle = (
                <div className="hui-text-body-secondary text-text-secondary">
                    {locationInfo.shippingStatusSecondary}
                </div>
            );
        }

        shippingInfo = (
            <>
                <H5>{locationInfo.shippingStatus}</H5>
                {subtitle}
            </>
        );
    }

    return (
        <section>
            {shippingInfo}
            <div className="hui-text-body-secondary text-text-secondary">
                {formatMessage(
                    {
                        id: 'wonitems.itemsByLocation.location',
                    },
                    {
                        itemCount: locationInfo.itemIds.length,
                        locationLink: (
                            <SubtleLink
                                href={mapsUrl}
                                size="sm"
                                target="_blank"
                            >
                                {formattedAddress}
                            </SubtleLink>
                        ),
                    }
                )}
            </div>
            <div>
                {locationInfo.itemIds.map((itemId) => {
                    const item = wonItems.find((item) => item.itemId === itemId);
                    if (item) {
                        return (
                            <StyledRow key={item.itemId}>
                                <WonItemRow
                                    buyersPremium={wonCatalog.buyersPremium}
                                    item={item}
                                    key={itemId}
                                    won
                                />
                            </StyledRow>
                        );
                    }
                    return null;
                })}
            </div>
        </section>
    );
};

export default WonItemLocation;

const StyledRow = styled.div`
    margin-top: 32px;
`;
